// Sidebar.tsx - Alternative simplified approach
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getMenuByGroups } from "@/constants/new-menu";
import { MenuItemType } from "@/constants/new-menu/types";
import SidebarMenu from "./SidebarMenu";
import UserProfileSidebar from "./UserProfileSidebar";
import { BsArrowsAngleContract, BsArrowsAngleExpand } from "react-icons/bs";
import { SidebarWrapper } from "@/styles";
import { FiUser, FiSettings, FiLogIn, FiHome } from "react-icons/fi";
import { useTranslation } from "react-i18next";

interface SidebarProps {
  isCollapsed: boolean;
  toggleSidebar: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, toggleSidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("commonNew");
  const { user, userGroupCodes, isAuthenticated } = useAuth();
  const [menuItems, setMenuItems] = useState<MenuItemType[]>([]);

  // Basic menu items always available for authenticated users
  const basicMenuItems: MenuItemType[] = [
    {
      key: "home",
      label: t("menu.home", "Trang chủ"),
      icon: FiHome,
      path: "/dashboard",
      roles: [],
    },
    {
      key: "profile",
      label: t("menu.profile", "Hồ sơ"),
      icon: FiUser,
      path: "/profile",
      roles: [],
    },
    {
      key: "settings",
      label: t("menu.settings", "Cài đặt"),
      icon: FiSettings,
      path: "/settings",
      roles: [],
    },
  ];

  // Menu for unauthenticated users
  const guestMenuItems: MenuItemType[] = [
    {
      key: "home",
      label: t("menu.home", "Trang chủ"),
      icon: FiHome,
      path: "/home",
      roles: ["guest"],
    },
    {
      key: "marketplace",
      label: t("menu.marketplace", "Marketplace"),
      icon: FiHome,
      path: "/marketplace",
      roles: ["guest"],
    },
    {
      key: "login",
      label: t("menu.login", "Đăng nhập"),
      icon: FiLogIn,
      path: "/login",
      roles: ["guest"],
    },
  ];

  useEffect(() => {
    // If not authenticated, show guest menu
    if (!isAuthenticated) {
      setMenuItems(guestMenuItems);
      return;
    }

    // Get role-based menu items
    let roleBasedItems = getMenuByGroups(userGroupCodes);

    // Special handling for documents page
    if (location.pathname.startsWith("/documents")) {
      const documentsMenu = roleBasedItems.find((item) => item.key === "documents");
      roleBasedItems = documentsMenu ? [documentsMenu] : [];
    }

    // If user has role-based permissions, show those menus
    if (roleBasedItems.length > 0) {
      setMenuItems(roleBasedItems);
    } else {
      // Otherwise, show basic menu items for authenticated users
      setMenuItems(basicMenuItems);
    }
  }, [user, userGroupCodes, location.pathname, isAuthenticated]);

  // Don't render sidebar at all if completely empty (edge case)
  if (menuItems.length === 0) {
    return null;
  }

  return (
    <SidebarWrapper $isCollapsed={isCollapsed}>
      {/* Toggle Button - Only show for authenticated users */}
      {isAuthenticated && (
        <button
          onClick={toggleSidebar}
          className={`
            absolute top-2 right-2 z-10
            p-2 rounded-lg
            bg-white text-blue-600
            hover:text-blue-700 hover:bg-blue-50
            transition-colors duration-200
            ${isCollapsed ? "hidden" : ""}
          `}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <BsArrowsAngleExpand size={24} />
          ) : (
            <BsArrowsAngleContract size={24} />
          )}
        </button>
      )}

      {/* User Profile Section - Only for authenticated */}
      {isAuthenticated && (
        <div className="flex-shrink-0">
          <UserProfileSidebar isCollapsed={isCollapsed} />
        </div>
      )}

      {/* Guest Logo/Brand Section - Only for guests */}
      {!isAuthenticated && !isCollapsed && (
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <img 
              src="/logo.svg" 
              alt="VIETRRO" 
              className="h-10 w-10"
            />
            <span className="text-xl font-bold text-blue-600">VIETRRO</span>
          </div>
        </div>
      )}

      {/* Menu Section */}
      <div className="flex-1 overflow-y-auto pb-3">
        <SidebarMenu
          menuItems={menuItems}
          isCollapsed={isCollapsed}
          fallback={toggleSidebar}
        />
      </div>

      {/* Footer Info for Limited Access */}
      {isAuthenticated && menuItems === basicMenuItems && !isCollapsed && (
        <div className="p-3 mx-3 mb-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-700 text-center">
            {t("permissions.basicAccess", "Truy cập cơ bản")}
          </p>
        </div>
      )}
    </SidebarWrapper>
  );
};

export default Sidebar;